/**
 * TeamSystem Impact - Data Capture Dashboard API Client
 * API calls for owner, supervisor, and admin dashboards
 */

import { apiClient } from '../../../api/client';
import type { DataPoint, DataPointListResponse, DataPointFilters } from '../../dma/api/datapoint.api';
import type { DataCaptureProgress, CampaignProgressResponse, OwnerByCampaignResponse, MyActivityResponse } from '@teamsystem-impact/shared';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const BASE_PATH = '/data-capture/dashboard';

export type { DataPoint, DataPointListResponse };

export const dashboardApi = {
  getOwnerDashboard: async (
    filters?: DataPointFilters & { page?: number; pageSize?: number }
  ): Promise<DataPointListResponse> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const response = await apiClient.get<ApiResponse<DataPointListResponse>>(
      `${BASE_PATH}/owner?${params.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get owner dashboard');
    return response.data.data!;
  },

  getSupervisorDashboard: async (
    filters?: DataPointFilters & { page?: number; pageSize?: number }
  ): Promise<DataPointListResponse> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const response = await apiClient.get<ApiResponse<DataPointListResponse>>(
      `${BASE_PATH}/supervisor?${params.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get supervisor dashboard');
    return response.data.data!;
  },

  importFromMaterialIROs: async (assessmentYear: number): Promise<{
    materialStandards: string[];
    materialIROCount: number;
    created: number;
    skipped: number;
    errors: number;
    details: Array<{ code: string; status: 'created' | 'skipped' | 'error'; error?: string; linkedIROId?: string }>;
  }> => {
    const response = await apiClient.post<ApiResponse<any>>(
      `${BASE_PATH}/import-from-material-iros`,
      { assessmentYear }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to import datapoints');
    return response.data.data!;
  },

  getProgress: async (assessmentYear?: number): Promise<DataCaptureProgress> => {
    const params = assessmentYear ? `?assessmentYear=${assessmentYear}` : '';
    const response = await apiClient.get<ApiResponse<DataCaptureProgress>>(
      `${BASE_PATH}/progress${params}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get progress');
    return response.data.data!;
  },

  getPhaseStatus: async (assessmentYear?: number): Promise<PhaseStatus> => {
    const params = assessmentYear ? `?assessmentYear=${assessmentYear}` : '';
    const response = await apiClient.get<ApiResponse<PhaseStatus>>(
      `${BASE_PATH}/phase-status${params}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get phase status');
    return response.data.data!;
  },

  getCampaignProgress: async (assessmentYear?: number): Promise<CampaignProgressResponse> => {
    const params = assessmentYear ? `?assessmentYear=${assessmentYear}` : '';
    const response = await apiClient.get<ApiResponse<CampaignProgressResponse>>(
      `${BASE_PATH}/campaign-progress${params}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get campaign progress');
    return response.data.data!;
  },

  getMyActivity: async (assessmentYear?: number, role?: 'owner' | 'validator' | 'all'): Promise<MyActivityResponse> => {
    const params = new URLSearchParams();
    if (assessmentYear) params.append('assessmentYear', String(assessmentYear));
    if (role) params.append('role', role);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<ApiResponse<MyActivityResponse>>(
      `${BASE_PATH}/my-activity${qs}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get activity history');
    return response.data.data!;
  },

  /** `campaignId` e' obbligatorio: dice al server quale riga guardare, perche'
   *  lo stesso datapoint puo' vivere in piu' campagne con owner diversi. */
  remindDataPoint: async (datapointId: string, campaignId: string): Promise<{ ownerName: string }> => {
    const response = await apiClient.post<ApiResponse<{ ownerName: string }>>(
      `${BASE_PATH}/datapoint/${datapointId}/remind`,
      { campaignId }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to send reminder');
    return response.data.data!;
  },

  notifyCampaign: async (campaignId: string): Promise<void> => {
    await apiClient.post(`/data-capture/campaigns/${campaignId}/remind`);
  },

  getOwnerByCampaign: async (assessmentYear?: number): Promise<OwnerByCampaignResponse> => {
    const params = assessmentYear ? `?assessmentYear=${assessmentYear}` : '';
    const response = await apiClient.get<ApiResponse<OwnerByCampaignResponse>>(
      `${BASE_PATH}/owner-by-campaign${params}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get owner by campaign');
    return response.data.data!;
  },
};

export interface PhaseStatus {
  configurationComplete: boolean;
  collectionComplete: boolean;
  datapointCount: number;
  assignmentCount: number;
}
